/**
 * 菜单编辑抽屉表单
 * 字段与后端 fnd_menus 表完全对齐
 */
import React, { useEffect, useState } from 'react';
import {
  Drawer, Form, Input, InputNumber, Radio, Switch,
  TreeSelect, Button, Space, message,
} from 'antd';
import {
  getMenuTree, createMenu, updateMenu,
} from '../../api/menu';
import type { MenuItem, MenuFormData, MenuType } from '../../api/types';
import { MenuType as MT } from '../../api/types';
import IconPicker from './IconPicker';

interface MenuFormProps {
  visible: boolean;
  menu: MenuItem | null;
  parentId: number | undefined;
  workMode?: string;
  onClose: () => void;
  onSuccess: () => void;
}

const MenuForm: React.FC<MenuFormProps> = ({
  visible,
  menu,
  parentId,
  workMode,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [menuTree, setMenuTree] = useState<MenuItem[]>([]);
  const [menuType, setMenuType] = useState<number>(MT.DIRECTORY);

  const isEdit = !!menu;

  /** 获取菜单树（用于上级菜单选择） */
  const fetchMenuTree = async () => {
    try {
      const res = await getMenuTree({ work_mode: workMode });
      if (res?.data) {
        const treeData = Array.isArray(res.data) ? res.data : (res.data as any).data || [];
        setMenuTree(treeData);
      }
    } catch (error) {
      console.error('获取菜单树失败', error);
    }
  };

  useEffect(() => {
    if (visible) {
      fetchMenuTree();
    }
  }, [visible]);

  /** 回填表单 / 初始化默认值 */
  useEffect(() => {
    if (!visible) return;

    if (isEdit && menu) {
      const type = menu.menu_type ?? MT.DIRECTORY;
      setMenuType(type);
      form.setFieldsValue({
        menu_type: type,
        parent_id: menu.parent_id || undefined,
        title: menu.title,
        name: menu.name,
        icon: menu.icon,
        path: menu.path,
        component: menu.component,
        redirect: menu.redirect,
        authority: menu.authority,
        sort_order: menu.sort_order ?? 0,
        hide: menu.hide === 1,
        keep_alive: menu.keep_alive === 1,
      });
    } else {
      // 新增模式
      setMenuType(MT.DIRECTORY);
      form.resetFields();
      form.setFieldsValue({
        menu_type: MT.DIRECTORY,
        parent_id: parentId || undefined,
        sort_order: 0,
        hide: false,
        keep_alive: true,
      });
    }
  }, [visible, menu, parentId, form, isEdit]);

  /** 菜单类型切换 */
  const handleTypeChange = (val: number) => {
    setMenuType(val);
  };

  /** 提交表单 */
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      const data: MenuFormData = {
        parent_id: values.parent_id || 0,
        name: values.name,
        title: values.title,
        menu_type: values.menu_type,
        path: values.path,
        component: values.component,
        redirect: values.redirect,
        icon: values.icon,
        authority: values.authority,
        sort_order: values.sort_order ?? 0,
        hide: values.hide ? 1 : 0,
        keep_alive: values.keep_alive ? 1 : 0,
        target: 0,
        work_mode: workMode || 'business',
      };

      if (isEdit) {
        await updateMenu(menu!.id, data);
        message.success('更新成功');
      } else {
        await createMenu(data);
        message.success('创建成功');
      }

      onSuccess();
    } catch (error) {
      console.error('提交失败', error);
    } finally {
      setSubmitting(false);
    }
  };

  const isDirectory = menuType === MT.DIRECTORY;
  const isMenu = menuType === MT.MENU;
  const isButton = menuType === MT.BUTTON;

  /** 将菜单树转换为 TreeSelect 需要的格式 */
  const convertToTreeSelectData = (items: MenuItem[]): any[] => {
    return items
      .filter(item => item.menu_type !== MT.BUTTON)
      .map(item => ({
        title: item.title,
        value: item.id,
        key: item.id,
        children: item.children ? convertToTreeSelectData(item.children) : [],
      }));
  };

  return (
    <Drawer
      title={isEdit ? '编辑菜单' : '新增菜单'}
      open={visible}
      onClose={onClose}
      width={520}
      destroyOnHidden
      extra={
        <Space>
          <Button onClick={onClose}>取消</Button>
          <Button type="primary" onClick={handleSubmit} loading={submitting}>
            提交
          </Button>
        </Space>
      }
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          menu_type: MT.DIRECTORY,
          sort_order: 0,
          hide: false,
          keep_alive: true,
        }}
      >
        {/* 菜单类型 */}
        <Form.Item
          name="menu_type"
          label="菜单类型"
          rules={[{ required: true, message: '请选择菜单类型' }]}
        >
          <Radio.Group onChange={(e) => handleTypeChange(e.target.value)}>
            <Radio.Button value={MT.DIRECTORY}>目录</Radio.Button>
            <Radio.Button value={MT.MENU}>菜单</Radio.Button>
            <Radio.Button value={MT.BUTTON}>按钮</Radio.Button>
          </Radio.Group>
        </Form.Item>

        {/* 上级菜单 */}
        <Form.Item
          name="parent_id"
          label="上级菜单"
        >
          <TreeSelect
            placeholder="请选择上级菜单（留空为顶级菜单）"
            treeData={convertToTreeSelectData(menuTree)}
            allowClear
            treeDefaultExpandAll
            style={{ width: '100%' }}
          />
        </Form.Item>

        {/* 菜单标题 */}
        <Form.Item
          name="title"
          label="菜单标题"
          rules={[{ required: true, message: '请输入菜单标题' }]}
        >
          <Input placeholder="请输入菜单标题" maxLength={50} />
        </Form.Item>

        {/* 路由名称 */}
        <Form.Item
          name="name"
          label="路由名称"
          rules={[
            { required: true, message: '请输入路由名称' },
            { pattern: /^[A-Za-z][A-Za-z0-9_-]*$/, message: '以字母开头，仅包含字母、数字、下划线和连字符' },
          ]}
        >
          <Input placeholder="请输入路由名称（唯一标识）" maxLength={100} />
        </Form.Item>

        {/* 图标（目录/菜单显示） */}
        {!isButton && (
          <Form.Item
            name="icon"
            label="图标"
          >
            <IconPicker />
          </Form.Item>
        )}

        {/* 路由路径（目录/菜单显示） */}
        {!isButton && (
          <Form.Item
            name="path"
            label="路由路径"
            rules={isMenu ? [{ required: true, message: '请输入路由路径' }] : undefined}
          >
            <Input placeholder="请输入路由路径，如 /tenant/system/menu" maxLength={200} />
          </Form.Item>
        )}

        {/* 组件路径（仅菜单） */}
        {isMenu && (
          <Form.Item
            name="component"
            label="组件路径"
          >
            <Input placeholder="请输入组件路径" maxLength={200} />
          </Form.Item>
        )}

        {/* 重定向（仅目录） */}
        {isDirectory && (
          <Form.Item
            name="redirect"
            label="重定向路径"
          >
            <Input placeholder="请输入重定向路径" maxLength={200} />
          </Form.Item>
        )}

        {/* 权限标识（仅按钮） */}
        {isButton && (
          <Form.Item
            name="authority"
            label="权限标识"
            rules={[{ required: true, message: '请输入权限标识' }]}
          >
            <Input placeholder="请输入权限标识，如 system:menu:add" maxLength={200} />
          </Form.Item>
        )}

        {/* 排序 */}
        <Form.Item
          name="sort_order"
          label="排序号"
        >
          <InputNumber min={0} max={9999} style={{ width: '100%' }} placeholder="排序号，数值越小越靠前" />
        </Form.Item>

        {/* 是否隐藏 */}
        <Form.Item
          name="hide"
          label="是否隐藏"
          valuePropName="checked"
        >
          <Switch checkedChildren="隐藏" unCheckedChildren="显示" />
        </Form.Item>

        {/* 是否缓存（仅菜单） */}
        {isMenu && (
          <Form.Item
            name="keep_alive"
            label="是否缓存"
            valuePropName="checked"
            tooltip="开启后页面切换时保持组件状态（keep-alive）"
          >
            <Switch checkedChildren="缓存" unCheckedChildren="不缓存" />
          </Form.Item>
        )}
      </Form>
    </Drawer>
  );
};

export default MenuForm;
